import React, { useState } from "react";
import { SpinnerIcon } from "./icons";
import { HighScore } from "../types";

interface EndScreenProps {
  score: number;
  totalQuestions: number;
  onRestart: () => void;
  highScores: HighScore[];
  addHighScore: (name: string, score: number) => void;
}

const EndScreen: React.FC<EndScreenProps> = ({
  score,
  totalQuestions,
  onRestart,
  highScores,
  addHighScore,
}) => {
  const [playerName, setPlayerName] = useState("");
  const [highScoreSubmitted, setHighScoreSubmitted] = useState(false);

  // Check if the current score qualifies for the leaderboard
  const isHighScore =
    score > 0 &&
    (highScores.length < 5 ||
      score > (highScores[highScores.length - 1]?.score ?? 0));

  const handleSaveScore = (e: React.FormEvent) => {
    e.preventDefault();
    if (playerName.trim()) {
      addHighScore(playerName.trim(), score);
      setHighScoreSubmitted(true);
    }
  };

  // If it's a high score and it hasn't been submitted yet, show the name entry form
  if (isHighScore && !highScoreSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center text-center p-8 bg-white rounded-2xl shadow-2xl max-w-2xl mx-auto border-4 border-yellow-400">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">
          New High Score!
        </h1>
        <p className="text-2xl text-gray-600 mb-6">
          You scored {score} points!
        </p>
        <form
          onSubmit={handleSaveScore}
          className="w-full flex flex-col items-center"
        >
          <label
            htmlFor="playerName"
            className="text-lg font-semibold text-gray-700 mb-2"
          >
            Enter your name:
          </label>
          <input
            id="playerName"
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            maxLength={10}
            className="text-center text-xl font-bold p-2 border-2 border-gray-300 rounded-lg w-full max-w-xs focus:border-teal-500 focus:ring-teal-500"
            placeholder="FORMULATOR"
            autoFocus
          />
          <button
            type="submit"
            disabled={!playerName.trim()}
            className="mt-6 bg-teal-500 text-white font-bold py-3 px-8 rounded-lg text-xl hover:bg-teal-600 transition-transform transform hover:scale-105 shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Save Score
          </button>
        </form>
      </div>
    );
  }

  const getSkillLevel = () => {
    if (score > 11000) return "Formulation Skills: Elite";
    if (score > 9500) return "Formulation Skills: Excellent";
    if (score > 8000) return "Formulation Skills: Pretty good";
    if (score >= 7000) return "Formulation Skills: Fair";
    return "Formulation Skills: Not quite there - try our course";
  };

  const success = score >= 7000;

  return (
    <div className="flex flex-col items-center justify-center text-center p-8 bg-white rounded-2xl shadow-2xl max-w-2xl mx-auto border-4 border-teal-500">
      <h1 className="text-4xl font-bold text-gray-800 mb-2">
        {success ? "Congratulations!" : "Good Effort!"}
      </h1>
      <p className="text-2xl text-gray-600 mb-4">You scored {score} points!</p>
      <div className="my-6 p-6 bg-teal-50 rounded-lg w-full">
        <h2 className="text-2xl font-bold text-teal-700">{getSkillLevel()}</h2>
      </div>

      <div className="flex flex-col items-center gap-4 w-full">
        <button
          onClick={onRestart}
          className="bg-teal-500 text-white font-bold py-2 px-6 rounded-lg text-lg hover:bg-teal-600 transition-transform transform hover:scale-105 shadow-lg flex items-center justify-center min-w-[240px]"
        >
          Try Again
        </button>
      </div>

      <div className="mt-8 pt-6 border-t border-gray-200 w-full">
        <p className="text-gray-600 mb-3">
          To learn more about formulating and become a cosmetic chemist, join
          our Practical Cosmetic Formulation course.
        </p>
        <a
          href="https://chemistscorner.com/learn-more/practical-cosmetic-formulating/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-orange-500 text-white font-bold py-3 px-8 rounded-lg text-xl hover:bg-orange-600 transition-transform transform hover:scale-105 shadow-lg"
        >
          Join the Course
        </a>
      </div>
    </div>
  );
};

export default EndScreen;
